import * as React from "react";
import { createLink } from "@tanstack/react-router";
import { Anchor  } from "@mantine/core";
import type {AnchorProps} from "@mantine/core";
import type { LinkComponent, LinkProps } from "@tanstack/react-router";

export interface NavLinkProps
  extends Omit<AnchorProps, "href" | "children" | "color">,
    Omit<LinkProps, "href" | "children"> {
  /**
   * Whether to apply default brand styling (underline on hover, brand colors)
   * @default false
   */
  withBrandStyling?: boolean;
  /**
   * Preload strategy for the link
   * @default "intent"
   */
  preload?: false | "intent";
  children?: React.ReactNode | ((state: { isActive: boolean; isTransitioning: boolean }) => React.ReactNode);
}

const MantineAnchorComponent = React.forwardRef<
  HTMLAnchorElement,
  NavLinkProps
>(({ withBrandStyling = false, preload = "intent", children, ...props }, ref) => {
  // Handle function children from Link component
  const resolvedChildren = typeof children === 'function' 
    ? children({ isActive: false, isTransitioning: false })
    : children;
  
  return <Anchor ref={ref} {...props}>{resolvedChildren}</Anchor>;
});

MantineAnchorComponent.displayName = "MantineAnchorComponent";

const CreatedLinkComponent = createLink(MantineAnchorComponent);

/**
 * NavLink - A reusable navigation link component that integrates TanStack Router
 * with Mantine's Anchor component.
 *
 * Best practices:
 * - Use for all internal navigation links
 * - Supports preloading for better UX
 * - Can apply brand styling or be unstyled
 *
 * @example
 * ```tsx
 * <NavLink to="/dashboard">Dashboard</NavLink>
 * <NavLink to="/posts/$postId" params={{ postId: "123" }} withBrandStyling>
 *   View Post
 * </NavLink>
 * ```
 */
export const NavLink: LinkComponent<typeof MantineAnchorComponent> = (
  props
) => {
  return <CreatedLinkComponent preload="intent" {...props} />;
};
